import { faChevronRight } from "@fortawesome/pro-solid-svg-icons";
import { Icon, Text, TextInput } from "@growtherapy/sprout-ui";
import classNames from "classnames";
import { useCallback, useMemo } from "react";
import { useParams } from "react-router-dom";
import type { PageComponentProps } from "../../../../components";
import { hashString, seededShuffle } from "../../../../utils";
import {
  MOOD_SURVEY_CUSTOM_RESPONSE_TITLE,
  MOOD_SURVEY_SUBTITLE,
  MOOD_SURVEY_CUSTOM_RESPONSE_PLACEHOLDER,
} from "./lang";
import {
  useMoodCheckinSelectionsState,
  useMoodCheckinCustomResponseState,
} from "./useMoodCheckinSelectionsState";
import {
  ChoiceValue,
  MOOD_CHECKIN_CHOICES,
  getChoicesForDisplay,
} from "./utils";

export function Content({ pageIndex, nextPage }: PageComponentProps<string[]>) {
  const { patientShortId = "" } = useParams();
  const [customResponse, setCustomResponse] =
    useMoodCheckinCustomResponseState();
  const [selections, setSelections] = useMoodCheckinSelectionsState();

  const shuffledChoicesForDisplay = useMemo(() => {
    const choicesForDisplay = getChoicesForDisplay(
      MOOD_CHECKIN_CHOICES,
      selections,
      pageIndex,
    );

    if (!choicesForDisplay) return;

    return seededShuffle(choicesForDisplay, hashString(patientShortId));
  }, [pageIndex, selections, patientShortId]);

  const onChoiceChange = useCallback(
    (choice: ChoiceValue) => {
      // take depth number of selections, then add the new choice
      setSelections((previousSelection) => [
        ...previousSelection.slice(0, pageIndex),
        choice,
      ]);

      nextPage();
    },
    [setSelections, pageIndex, nextPage],
  );

  const onCustomInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setCustomResponse(e.target.value);
    },
    [setCustomResponse],
  );

  if (!shuffledChoicesForDisplay) return null;

  const listItemClassName =
    "py-2 px-4 border rounded-sm rebrand:border-border-default rebrand:rounded-200-250";

  return (
    <>
      <Text variant="sm" className="text-neutral-700 rebrand:text-text-muted">
        {MOOD_SURVEY_SUBTITLE}
      </Text>

      <ul className="flex flex-col gap-3 mt-4">
        {shuffledChoicesForDisplay.map(({ value, label = value, children }) => (
          <li
            key={value}
            className={classNames(listItemClassName, {
              "bg-neutral-300 rebrand:border-selected rebrand:bg-surface-state-selected-brand":
                selections[pageIndex] === value,
              "hover:bg-neutral-200 rebrand:hover:bg-surface-state-hover-default":
                selections[pageIndex] !== value,
            })}
          >
            <button
              onClick={() => onChoiceChange(value)}
              className="flex justify-between w-full items-center rebrand:text-text-default"
            >
              <span
                className="font-medium fs-exclude rebrand:typography-body-emphasis-default"
                data-dd-privacy="mask"
                data-dd-action-name="Click on mood checkin choice"
              >
                {label}
              </span>
              {(children?.length ?? 0) > 0 && (
                <Icon aria-hidden icon={faChevronRight} />
              )}
            </button>
          </li>
        ))}

        {pageIndex === 0 && (
          <li
            key={MOOD_SURVEY_CUSTOM_RESPONSE_TITLE}
            className={listItemClassName}
          >
            <div className="flex justify-between items-center w-full rebrand:text-text-default">
              <span className="font-medium fs-exclude rebrand:typography-body-emphasis-default">
                {MOOD_SURVEY_CUSTOM_RESPONSE_TITLE}
              </span>
              <Icon aria-hidden icon={faChevronRight} />
            </div>
            <div>
              <TextInput
                className="mt-2 py-2"
                mask="exclude"
                value={customResponse}
                onChange={onCustomInputChange}
                placeholder={MOOD_SURVEY_CUSTOM_RESPONSE_PLACEHOLDER}
              />
            </div>
          </li>
        )}
      </ul>
    </>
  );
}
